import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Center,
  Flex,
  Grid,
  GridItem,
  Image,
  Skeleton,
  SkeletonText,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useParams } from "react-router-dom";
import { apiGet } from "../../Backend/api";
import { IntroVideoDrawer } from "../../UI/Mini/IntroVideoDrawer";
import { LookupDrawer } from "./LookupDrawer";

// Same image-handler base used everywhere else in the v2 surface for
// owner-uploaded property images. Inlined intentionally — there's no
// shared helper today, and Part 1 of the QR flow shouldn't introduce
// a refactor that touches every other call site.
const CLOUDFRONT_BASE_URL = "https://dl6ri8ttf37m3.cloudfront.net";

const buildOwnerImageUrl = (key) => {
  if (!key) return null;
  if (typeof key !== "string") return null;
  if (key.startsWith("http")) return key;
  return `${CLOUDFRONT_BASE_URL}/public/${encodeURIComponent(key)}?width=600&format=webp`;
};

// Shared hero layout: on mobile the grid is sized to the *visible* viewport
// (100dvh — excludes the browser's URL/bookmark bars) with the content
// centered between flexible spacer rows and the CTA pinned to the bottom
// edge, so everything fits in a single frame. Desktop keeps the
// split-screen text/image hero.
const heroGridProps = {
  position: "relative",
  minH: "100vh",
  sx: { "@supports (height: 100dvh)": { minHeight: "100dvh" } },
  maxW: "1160px",
  mx: "auto",
  px: { base: 8, lg: 16 },
  alignContent: { base: "stretch", lg: "center" },
  templateAreas: {
    base: `"." "head" "image" "tagline" "." "cta"`,
    lg: `"head image" "tagline image" "cta image"`,
  },
  templateRows: {
    base: "1fr auto auto auto 1fr auto",
    lg: "auto auto auto",
  },
  templateColumns: { base: "1fr", lg: "minmax(0, 1fr) auto" },
  columnGap: { lg: 20 },
};

// CTA row sticks to the bottom of the visible frame on mobile, with a fade
// backdrop so content never shows through behind it.
const heroCtaProps = {
  area: "cta",
  mt: { base: 4, lg: 10 },
  position: { base: "sticky", lg: "static" },
  bottom: 0,
  zIndex: 1,
  bg: {
    base: "linear-gradient(180deg, rgba(26, 32, 44, 0) 0%, #1A202C 35%)",
    lg: "none",
  },
  // Pull the CTA row out of the grid gutter (32px) so the button spans
  // wider than the content, with its own 16px side padding.
  mx: { base: -8, lg: 0 },
  px: { base: 4, lg: 0 },
  pt: { base: 3, lg: 0 },
  pb: { base: "max(12px, env(safe-area-inset-bottom))", lg: 0 },
};

const heroImageSize = {
  base: "clamp(170px, 28vh, 250px)",
  md: "300px",
  lg: "400px",
};
const heroImageRadius = { base: "20px", lg: "28px" };

const HeroGlow = () => (
  <Box
    display={{ base: "none", lg: "block" }}
    position="absolute"
    inset={0}
    pointerEvents="none"
    background="radial-gradient(760px circle at 68% 42%, rgba(129, 126, 255, 0.16), transparent 65%)"
  />
);

const STATUS_LOADING = "loading";
const STATUS_OK = "ok";
const STATUS_NOT_FOUND = "notFound";
const STATUS_INACTIVE = "inactive";
const STATUS_ERROR = "error";

export const LookupLanding = () => {
  const { ownerId } = useParams();

  const [status, setStatus] = useState(STATUS_LOADING);
  const [owner, setOwner] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);

  // Lookup drawer = Part 2. Opens from the primary CTA below; closes back
  // here unless the guest confirms a match (in which case the drawer's
  // own seam navigates away to /go?bID=...).
  const {
    isOpen: lookupOpen,
    onOpen: openLookup,
    onClose: closeLookup,
  } = useDisclosure();

  const fetchOwner = useCallback(async () => {
    if (!ownerId) {
      setStatus(STATUS_ERROR);
      return;
    }

    setStatus(STATUS_LOADING);
    setImageLoaded(false);
    setImageError(false);

    try {
      // Public endpoint — uses x-api-key only (no Cognito session yet).
      const response = await apiGet(`/users/public/${ownerId}`);

      if (response?.success && response?.data?.owner) {
        const fetchedOwner = response.data.owner;
        setOwner(fetchedOwner);

        // Treat anything other than ACTIVE the same way for V1 — locked.
        if (fetchedOwner.status !== "ACTIVE") {
          setStatus(STATUS_INACTIVE);
        } else {
          setStatus(STATUS_OK);
        }
      } else {
        setStatus(STATUS_NOT_FOUND);
      }
    } catch (err) {
      const code = err?.response?.status;
      if (code === 404 || code === 400) {
        setStatus(STATUS_NOT_FOUND);
      } else {
        setStatus(STATUS_ERROR);
      }
    }
  }, [ownerId]);

  useEffect(() => {
    fetchOwner();
  }, [fetchOwner]);

  const ownerImageUrl = buildOwnerImageUrl(owner?.images);

  // Preload owner image so the swap from skeleton is clean — same pattern
  // LandingV2 uses for booking property images.
  useEffect(() => {
    if (!ownerImageUrl) return;
    const img = new window.Image();
    img.onload = () => setImageLoaded(true);
    img.onerror = () => {
      setImageError(true);
      setImageLoaded(true);
    };
    img.src = ownerImageUrl;
  }, [ownerImageUrl]);

  const hasIntroVideo =
    !!owner?.extendedMetas?.hasIntroVideo &&
    !!owner?.extendedMetas?.introVideoUrl;

  const handleCta = () => {
    if (!owner || status !== STATUS_OK) return;
    openLookup();
  };

  // ── Terminal states (no CTA, no skeletons) ────────────────────────────────

  if (status === STATUS_NOT_FOUND) {
    return (
      <Center h="100vh" flexDirection="column" px={8}>
        <Text
          fontSize="2xl"
          fontWeight="bold"
          mb={4}
          className="font-gilroy-bold"
          textAlign="center"
        >
          Property not found
        </Text>
        <Text color="gray.500" textAlign="center" fontSize="md">
          We couldn't find this property. Double-check the QR code or contact the host.
        </Text>
      </Center>
    );
  }

  if (status === STATUS_INACTIVE) {
    return (
      <Center h="100vh" flexDirection="column" px={8}>
        <Text
          fontSize="2xl"
          fontWeight="bold"
          mb={4}
          className="font-gilroy-bold"
          textAlign="center"
        >
          Bookings unavailable
        </Text>
        <Text color="gray.500" textAlign="center" fontSize="md">
          This property isn't accepting bookings right now. Please contact the host.
        </Text>
      </Center>
    );
  }

  if (status === STATUS_ERROR) {
    return (
      <Center h="100vh" flexDirection="column" px={8}>
        <Text
          fontSize="2xl"
          fontWeight="bold"
          mb={4}
          className="font-gilroy-bold"
          textAlign="center"
        >
          Something went wrong
        </Text>
        <Text color="gray.500" textAlign="center" fontSize="md" mb={6}>
          We couldn't load this property. Try again in a moment.
        </Text>
        <Button
          color="white"
          bg="#817EFF"
          _hover={{ backgroundColor: "#504CFF" }}
          px={8}
          py={6}
          borderRadius="60px"
          onClick={fetchOwner}
        >
          Try again
        </Button>
      </Center>
    );
  }

  // ── Loading ───────────────────────────────────────────────────────────────
  // Render a skeleton for every visible affordance, including the CTA, so
  // the button doesn't pop in before the data lands (test plan row 4).

  if (status === STATUS_LOADING) {
    return (
      <Box
        minH="100vh"
        position="relative"
        overflow={{ base: "visible", lg: "hidden" }}
        sx={{ "@supports (height: 100dvh)": { minHeight: "100dvh" } }}
      >
        <HeroGlow />
        <Grid {...heroGridProps}>
          <GridItem area="head" alignSelf={{ lg: "end" }}>
            <Text className="font-gilroy-bold" fontSize={{ lg: "18px" }}>
              Welcome to
            </Text>
            <Skeleton
              w={{ base: "220px", lg: "320px" }}
              h="44px"
              borderRadius="10px"
              mt={2}
            />
          </GridItem>

          <GridItem
            area="image"
            justifySelf={{ base: "center", lg: "end" }}
            alignSelf="center"
            mt={{ base: 6, lg: 0 }}
          >
            <Skeleton w={heroImageSize} h={heroImageSize} borderRadius={heroImageRadius} />
          </GridItem>

          <GridItem area="tagline" mt={{ base: 5, lg: 6 }}>
            <Flex
              flexDirection="column"
              alignItems={{ base: "center", lg: "flex-start" }}
            >
              <Skeleton w="220px" h="28px" borderRadius="6px" />
              <SkeletonText
                mt={3}
                noOfLines={1}
                skeletonHeight="3"
                w="260px"
              />
            </Flex>
          </GridItem>

          <GridItem {...heroCtaProps}>
            <Skeleton
              w={{ base: "full", lg: "280px" }}
              h="56px"
              borderRadius="10px"
            />
          </GridItem>
        </Grid>
      </Box>
    );
  }

  // ── ok ────────────────────────────────────────────────────────────────────

  const showImageSkeleton = ownerImageUrl && !imageLoaded && !imageError;
  const showImage = ownerImageUrl && imageLoaded && !imageError;

  return (
    <>
      <Box
        minH="100vh"
        position="relative"
        overflow={{ base: "visible", lg: "hidden" }}
        sx={{ "@supports (height: 100dvh)": { minHeight: "100dvh" } }}
      >
        <HeroGlow />
        <Grid {...heroGridProps}>
          <GridItem area="head" alignSelf={{ lg: "end" }}>
            <Text className="font-gilroy-bold" fontSize={{ lg: "18px" }}>
              Welcome to
            </Text>
            <Text
              className="font-gilroy-bold"
              fontSize={{ base: "32px", lg: "46px" }}
              lineHeight={{ lg: "1.15" }}
            >
              {owner?.name || "Custom Branding"}
            </Text>
          </GridItem>

          <GridItem
            area="image"
            justifySelf={{ base: "center", lg: "end" }}
            alignSelf="center"
            mt={{ base: 6, lg: 0 }}
          >
            {showImageSkeleton ? (
              <Skeleton w={heroImageSize} h={heroImageSize} borderRadius={heroImageRadius} />
            ) : showImage ? (
              <Image
                src={ownerImageUrl}
                borderRadius={heroImageRadius}
                w={heroImageSize}
                h={heroImageSize}
                objectFit="cover"
                loading="eager"
                boxShadow={{ lg: "0 30px 80px rgba(129, 126, 255, 0.25)" }}
              />
            ) : (
              // Neutral placeholder when there's no image OR it failed to
              // load. Screen must remain usable per test plan row 7.
              <Center
                w={heroImageSize}
                h={heroImageSize}
                borderRadius={heroImageRadius}
                bg="rgba(129, 126, 255, 0.12)"
              >
                <Text
                  fontSize={{ base: "64px", lg: "96px" }}
                  className="font-gilroy-bold"
                  color="#817EFF"
                >
                  {owner?.name?.[0]?.toUpperCase() || "•"}
                </Text>
              </Center>
            )}
          </GridItem>

          <GridItem area="tagline" mt={{ base: 5, lg: 6 }}>
            <Flex
              flexDirection="column"
              alignItems={{ base: "center", lg: "flex-start" }}
            >
              <Text
                className="font-gilroy-bold"
                fontSize={{ base: "24px", lg: "26px" }}
              >
                Ready when you are
              </Text>
              <Text
                mt={2}
                textAlign={{ base: "center", lg: "left" }}
                color="#DFDFDF"
                fontWeight={500}
                fontSize={{ base: "16px", lg: "17px" }}
              >
                Look up your booking to continue.
              </Text>

              {hasIntroVideo && (
                <Button
                  variant="link"
                  mt={4}
                  color="#817EFF"
                  fontWeight={600}
                  onClick={() => setVideoOpen(true)}
                >
                  Watch a quick intro
                </Button>
              )}
            </Flex>
          </GridItem>

          <GridItem {...heroCtaProps}>
            <Button
              color="white"
              bg="#817EFF"
              _hover={{ transform: "translateY(-1px)" }}
              _active={{ transform: "scale(0.97)" }}
              w={{ base: "full", lg: "auto" }}
              minW={{ lg: "280px" }}
              py={6}
              px={{ base: 6, lg: 12 }}
              borderRadius="10px"
              // Same lively gradient + glow treatment as the check-in
              // instructions button.
              sx={{
                backgroundImage:
                  "linear-gradient(270deg,#8B5CF6,#7C3AED,#6366F1,#A855F7,#8B5CF6)",
                backgroundSize: "300% 300%",
                animation:
                  "ctaGradient 4s ease infinite, ctaGlow 2.5s ease-in-out infinite",
                "@keyframes ctaGradient": {
                  "0%": { backgroundPosition: "0% 50%" },
                  "50%": { backgroundPosition: "100% 50%" },
                  "100%": { backgroundPosition: "0% 50%" },
                },
                "@keyframes ctaGlow": {
                  "0%, 100%": { boxShadow: "0 0 0 0 rgba(139,92,246,0)" },
                  "50%": { boxShadow: "0 0 18px 4px rgba(139,92,246,0.4)" },
                },
                transition: "transform 0.15s ease",
              }}
              onClick={handleCta}
            >
              Look up my booking
              <Text
                as="span"
                ml="6px"
                display="inline-flex"
                alignItems="center"
                sx={{
                  animation: "ctaNudge 1.2s ease-in-out infinite",
                  "@keyframes ctaNudge": {
                    "0%, 100%": { transform: "translateX(0)" },
                    "50%": { transform: "translateX(4px)" },
                  },
                }}
              >
                ›
              </Text>
            </Button>
          </GridItem>
        </Grid>
      </Box>

      {hasIntroVideo && (
        <IntroVideoDrawer
          isOpen={videoOpen}
          onClose={() => setVideoOpen(false)}
          videoUrl={owner.extendedMetas.introVideoUrl}
          propertyName={owner.name}
        />
      )}

      <LookupDrawer
        isOpen={lookupOpen}
        onClose={closeLookup}
        ownerId={ownerId}
      />
    </>
  );
};

export default LookupLanding;
